import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormWatch,
} from 'react-hook-form'

import s from './styles.module.scss'
type MinMaxInputFieldsProps = {
  minLimit?: number
  maxLimit?: number
  initialMin?: string
  initialMax?: string
  control: Control<any, any>
  minName: string
  maxName: string
  minUnit?: string
  maxUnit?: string
  minValue?: number
  maxValue?: number
  minStepRange?: number
  allowEmptyField?: boolean
  getValues: UseFormGetValues<any>
  watch: UseFormWatch<any>
  onInputChangeCallback?: (value: number, isMin: boolean) => void
}
export const MinMaxInputFields: React.FC<MinMaxInputFieldsProps> = ({
  minLimit = 0,
  maxLimit = 100,
  control,
  minName,
  maxName,
  initialMin,
  initialMax,
  minUnit,
  maxUnit,
  minValue,
  maxValue,
  allowEmptyField = true,
  minStepRange,
  getValues,
  watch,
  onInputChangeCallback,
}) => {
  const [minInputValue, setMinInputValue] = useState(initialMin || '')
  const [maxInputValue, setMaxInputValue] = useState(initialMax || '')

  const validateAndSetValues = (isMin: boolean): number | undefined => {
    if (allowEmptyField) {
      if (isMin && !minInputValue) {
        return undefined
      }
      if (!isMin && !maxInputValue) {
        return undefined
      }
    }
    let minNum = parseInt(minInputValue, 10)
    let maxNum = parseInt(maxInputValue, 10)
    if (isNaN(minNum)) {
      minNum = minLimit
    }
    if (isNaN(maxNum)) {
      maxNum = maxLimit
    }
    if (isMin) {
      minNum = Math.max(
        minLimit,
        Math.min(minNum, maxNum - (minStepRange || 0)),
      )
      setMinInputValue(String(minNum))
      onInputChangeCallback?.(minNum, isMin)
      return minNum
    } else {
      maxNum = Math.min(
        maxLimit,
        Math.max(maxNum, minNum + (minStepRange || 0)),
      )
      setMaxInputValue(String(maxNum))
      onInputChangeCallback?.(maxNum, isMin)
      return maxNum
    }
  }
  const handleInputChange = (value: string, isMin: boolean) => {
    if (isMin) {
      setMinInputValue(value)
    } else {
      setMaxInputValue(value)
    }
  }
  useEffect(() => {
    const formMinValue = getValues(minName)
    const formMaxValue = getValues(maxName)
    if (formMinValue !== minInputValue) {
      setMinInputValue(formMinValue ?? '')
    }
    if (formMaxValue !== maxInputValue) {
      setMaxInputValue(formMaxValue ?? '')
    }
  }, [watch(minName), watch(maxName)])
  useEffect(() => {
    if (minValue !== undefined) {
      setMinInputValue(String(minValue))
    }
  }, [minValue])

  useEffect(() => {
    if (maxValue !== undefined) {
      setMaxInputValue(String(maxValue))
    }
  }, [maxValue])

  return (
    <div className={s.container}>
      <div className={s.priceInputContainer}>
        <div className={s.priceInput}>
          <div className={s.MinMaxTitle}>Min</div>
          <Controller
            control={control}
            name={minName}
            render={({ field }) => (
              <div className={clsx(s.inputUnitContainer)}>
                <input
                  type='number'
                  value={minInputValue}
                  onChange={(e) => handleInputChange(e.target.value, true)}
                  onBlur={() => field.onChange(validateAndSetValues(true))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      field.onChange(validateAndSetValues(true))
                    }
                  }}
                />
                {minUnit && <span>{minUnit}</span>}
              </div>
            )}
          />
        </div>
        <div className={s.priceInput}>
          <div className={s.MinMaxTitle}>Max</div>
          <Controller
            control={control}
            name={maxName}
            render={({ field }) => (
              <div className={clsx(s.inputUnitContainer)}>
                <input
                  type='number'
                  value={maxInputValue}
                  onChange={(e) => handleInputChange(e.target.value, false)}
                  onBlur={() => field.onChange(validateAndSetValues(false))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      field.onChange(validateAndSetValues(false))
                    }
                  }}
                />
                {minUnit && maxUnit && (
                  <span>
                    {parseInt(maxInputValue) === maxLimit ? maxUnit : minUnit}
                  </span>
                )}
              </div>
            )}
          />
        </div>
      </div>
    </div>
  )
}
